import { ErrorMessage, Formik } from 'formik'
import { observer } from 'mobx-react-lite'
import { Link } from 'react-router-dom'
import { useStore } from '../../app/stores/store'
import { useLegalText } from '../../app/hooks/useLegalText'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import AuthShell from '../../app/common/ui/AuthShell'
import { AppButton, AppCheckbox, AppInput, AppPasswordInput, AppStack, AppText } from '../../app/common/ui'

function friendlyValidationMessage (field: string, message: string): string {
  const key = field.toLowerCase()
  if (key === 'password' && /match the regular expression|regex/.test(message)) { return 'Password must be 6–100 characters and include at least one number, one lowercase letter, and one uppercase letter.' }
  if (key === 'username' && /already taken|duplicate|taken/i.test(message)) { return 'That username is already taken.' }
  if (key === 'email' && /already taken|duplicate|taken/i.test(message)) { return 'Email taken' }
  if (key === 'termsaccepted') { return 'You must accept the Terms of Service.' }
  if (key === 'privacypolicyaccepted') { return 'You must accept the Privacy Policy.' }
  return message.replace(/^The field \w+ /, '').trim() || message
}

function formatRegistrationError (error: any): string {
  const data = error?.response?.data
  if (!data) return error?.message || 'Registration failed.'
  if (data.detail && typeof data.detail === 'string') return data.detail
  const errs = data.errors
  if (errs && typeof errs === 'object') {
    const lines: string[] = []
    for (const key of Object.keys(errs)) {
      const messages = Array.isArray(errs[key]) ? errs[key] : [errs[key]]
      const friendly = messages.map((m: string) => friendlyValidationMessage(key, m))
      lines.push(...friendly)
    }
    if (lines.length) return lines.join(' ')
  }
  return 'Registration failed. Please check your details and try again.'
}

export default observer(function RegisterForm () {
  const { userStore } = useStore()
  const { text: termsText, loading: termsLoading } = useLegalText('terms-of-service')
  const { text: privacyText, loading: privacyLoading } = useLegalText('privacy-policy')

  return (
    <AuthShell title="CREATE ACCOUNT">
      <Formik
        initialValues={{
          displayName: '',
          username: '',
          email: '',
          password: '',
          termsAccepted: false,
          privacyPolicyAccepted: false,
          error: ''
        }}
        onSubmit={async (values, { setErrors }) => {
          if (!termsText || !privacyText) {
            setErrors({ error: 'Unable to load consent documents. Please try again.' })
            return
          }
          try {
            await userStore.register({
              displayName: values.displayName,
              username: values.username,
              email: values.email,
              password: values.password,
              termsAccepted: values.termsAccepted,
              termsVersionAccepted: termsText.version,
              privacyPolicyAccepted: values.privacyPolicyAccepted,
              privacyPolicyVersionAccepted: privacyText.version
            })
          } catch (error: any) {
            const friendly = formatRegistrationError(error)
            setErrors({ error: friendly })
            if (friendly) toast.error(friendly)
          }
        }}
        validationSchema={Yup.object({
          displayName: Yup.string().required('Display name is required'),
          username: Yup.string().required('Username is required'),
          email: Yup.string().required('Email is required').email('Invalid email'),
          password: Yup.string().required('Password is required'),
          termsAccepted: Yup.boolean().oneOf([true], 'You must accept the Terms of Service'),
          privacyPolicyAccepted: Yup.boolean().oneOf([true], 'You must accept the Privacy Policy')
        })}
      >
        {({ handleSubmit, isSubmitting, errors, isValid, dirty, values, handleChange, handleBlur, touched, setFieldValue }) => (
          <form onSubmit={handleSubmit} autoComplete="off">
            <AppStack spacing="sm">
              <AppInput
                name="displayName"
                placeholder="Display Name"
                value={values.displayName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.displayName && (errors as any).displayName}
              />
              <AppInput
                name="username"
                placeholder="Username"
                value={values.username}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.username && (errors as any).username}
              />
              <AppInput
                name="email"
                placeholder="Email"
                type="email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.email && (errors as any).email}
              />
              <AppPasswordInput
                name="password"
                placeholder="Password"
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.password && (errors as any).password}
                description="6–100 characters, with at least one number, one lowercase letter, and one uppercase letter"
              />
              <AppCheckbox
                label={
                  <>
                    I agree to the{' '}
                    <Link to="/terms" target="_blank" style={{ color: '#58A6FF' }}>
                      Terms of Service
                    </Link>
                    {termsText && ` (v${termsText.version})`}
                  </>
                }
                checked={values.termsAccepted}
                onChange={(e) => setFieldValue('termsAccepted', e.currentTarget.checked)}
                disabled={termsLoading || !termsText}
                error={touched.termsAccepted && (errors as any).termsAccepted}
              />
              <AppCheckbox
                label={
                  <>
                    I accept the{' '}
                    <Link to="/privacy" target="_blank" style={{ color: '#58A6FF' }}>
                      Privacy Policy
                    </Link>
                    {privacyText && ` (v${privacyText.version})`}
                  </>
                }
                checked={values.privacyPolicyAccepted}
                onChange={(e) => setFieldValue('privacyPolicyAccepted', e.currentTarget.checked)}
                disabled={privacyLoading || !privacyText}
                error={touched.privacyPolicyAccepted && (errors as any).privacyPolicyAccepted}
              />
              <ErrorMessage
                name="error"
                render={() => (
                  <AppText size="sm" color="red" mb="xs">
                    {errors.error}
                  </AppText>
                )}
              />
              <AppButton
                type="submit"
                disabled={!isValid || !dirty || isSubmitting || termsLoading || !termsText || privacyLoading || !privacyText}
                loading={isSubmitting}
                fullWidth
                size="md"
              >
                Create Account
              </AppButton>
            </AppStack>
          </form>
        )}
      </Formik>

      <AppText size="xs" ta="center" mt="lg" sx={{ color: 'var(--gs-muted)' }}>
        Already have an account?{' '}
        <AppText component={Link} to="/login" sx={{ color: '#58A6FF' }}>
          Sign in →
        </AppText>
      </AppText>
    </AuthShell>
  )
})
